import { useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { Input } from "../../../components/Input";
import { SelectInput } from "../../../components/SelectInput";
import { Toggle } from "../../../components/Toggle";
import { FormSection } from "../../../components/FormSection";
import { OfferType } from "@KassaroAB/shared/types/enums/OfferType";
import { Fields, HoursDay, HoursGrid } from "./offerFormStyles";
import {
  createDefaultWeeklyHours,
  OFFER_TYPES,
  offerTypeLabel,
  WEEK_DAYS,
} from "./offerUtils";

interface OfferCommonFieldsProps {
  // Shared across type-specific tanstack forms with different value shapes.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  isEditing: boolean;
  onTypeChange?: (type: OfferType) => void;
}

export const OfferCommonFields = ({
  form,
  isEditing,
  onTypeChange,
}: OfferCommonFieldsProps) => {
  const { t } = useTranslation();
  const useSchedule = useStore(
    form.store,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.values.useSchedule as boolean,
  );
  const useHours = useStore(
    form.store,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.values.useHours as boolean,
  );

  return (
    <>
      <FormSection title={t("offerForm.basicInformation")}>
        <Fields>
          <form.Field name="name">
            {(field: {
              state: { value: string };
              handleChange: (v: string) => void;
              handleBlur: () => void;
            }) => (
              <FormGroup>
                <FormLabel>{t("offerForm.name")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="receiptText">
            {(field: {
              state: { value: string };
              handleChange: (v: string) => void;
              handleBlur: () => void;
            }) => (
              <FormGroup>
                <FormLabel>{t("offerForm.receiptText")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="type">
            {(field: {
              state: { value: OfferType };
              handleChange: (v: OfferType) => void;
              handleBlur: () => void;
            }) => (
              <FormGroup>
                <FormLabel>{t("offerForm.type")}</FormLabel>
                <SelectInput
                  value={field.state.value}
                  disabled={isEditing}
                  onChange={(e) => {
                    const next = e.target.value as OfferType;
                    if (!isEditing && onTypeChange) {
                      onTypeChange(next);
                      return;
                    }
                    field.handleChange(next);
                  }}
                  onBlur={field.handleBlur}
                >
                  {OFFER_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {offerTypeLabel(type, t)}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="enabled">
            {(field: {
              state: { value: boolean };
              handleChange: (v: boolean) => void;
              handleBlur: () => void;
            }) => (
              <FormGroup>
                <FormLabel>{t("offerForm.enabled")}</FormLabel>
                <Toggle
                  checked={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="priority">
            {(field: {
              state: { value: number };
              handleChange: (v: number) => void;
              handleBlur: () => void;
            }) => (
              <FormGroup>
                <FormLabel>{t("offerForm.priority")}</FormLabel>
                <Input
                  type="number"
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(
                      e.target.value === "" ? 0 : Number(e.target.value),
                    )
                  }
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>

      <FormSection title={t("offerForm.schedule")}>
        <Fields>
          <form.Field name="useSchedule">
            {(field: {
              state: { value: boolean };
              handleChange: (v: boolean) => void;
              handleBlur: () => void;
            }) => (
              <FormGroup>
                <FormLabel>{t("offerForm.useSchedule")}</FormLabel>
                <Toggle
                  checked={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          {useSchedule ? (
            <>
              <form.Field name="startsAt">
                {(field: {
                  state: { value: string };
                  handleChange: (v: string) => void;
                  handleBlur: () => void;
                }) => (
                  <FormGroup>
                    <FormLabel>{t("offerForm.startsAt")}</FormLabel>
                    <Input
                      type="date"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormGroup>
                )}
              </form.Field>
              <form.Field name="endsAt">
                {(field: {
                  state: { value: string };
                  handleChange: (v: string) => void;
                  handleBlur: () => void;
                }) => (
                  <FormGroup>
                    <FormLabel>{t("offerForm.endsAt")}</FormLabel>
                    <Input
                      type="date"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormGroup>
                )}
              </form.Field>
              <form.Field name="useHours">
                {(field: {
                  state: { value: boolean };
                  handleChange: (v: boolean) => void;
                  handleBlur: () => void;
                }) => (
                  <FormGroup>
                    <FormLabel>{t("offerForm.useHours")}</FormLabel>
                    <Toggle
                      checked={field.state.value}
                      onChange={(checked) => {
                        field.handleChange(checked);
                        if (checked) {
                          form.setFieldValue(
                            "weekly",
                            (current: unknown) =>
                              current ?? createDefaultWeeklyHours(),
                          );
                        }
                      }}
                      onBlur={field.handleBlur}
                    />
                  </FormGroup>
                )}
              </form.Field>
              {useHours ? (
                <form.Field name="weekly">
                  {(field: {
                    state: {
                      value: Record<
                        (typeof WEEK_DAYS)[number],
                        | { closed: true }
                        | {
                            closed: false;
                            ranges: { open: string; close: string }[];
                          }
                      >;
                    };
                    handleChange: (v: typeof field.state.value) => void;
                  }) => (
                    <Fields>
                      {WEEK_DAYS.map((day) => {
                        const daySchedule = field.state.value[day];
                        const closed = daySchedule.closed;
                        const open =
                          !closed && daySchedule.ranges[0]
                            ? daySchedule.ranges[0].open
                            : "09:00";
                        const close =
                          !closed && daySchedule.ranges[0]
                            ? daySchedule.ranges[0].close
                            : "17:00";
                        return (
                          <HoursGrid key={day}>
                            <HoursDay>
                              {t(`offerForm.weekdays.${day}`)}
                            </HoursDay>
                            <Toggle
                              checked={!closed}
                              onChange={(active) => {
                                field.handleChange({
                                  ...field.state.value,
                                  [day]: active
                                    ? {
                                        closed: false as const,
                                        ranges: [{ open, close }],
                                      }
                                    : { closed: true as const },
                                });
                              }}
                            />
                            <Input
                              type="time"
                              value={open}
                              disabled={closed}
                              onChange={(e) => {
                                field.handleChange({
                                  ...field.state.value,
                                  [day]: {
                                    closed: false as const,
                                    ranges: [
                                      { open: e.target.value, close },
                                    ],
                                  },
                                });
                              }}
                            />
                            <Input
                              type="time"
                              value={close}
                              disabled={closed}
                              onChange={(e) => {
                                field.handleChange({
                                  ...field.state.value,
                                  [day]: {
                                    closed: false as const,
                                    ranges: [
                                      { open, close: e.target.value },
                                    ],
                                  },
                                });
                              }}
                            />
                          </HoursGrid>
                        );
                      })}
                    </Fields>
                  )}
                </form.Field>
              ) : null}
            </>
          ) : null}
        </Fields>
      </FormSection>
    </>
  );
};
